import { PlanOption, SubscriptionData } from '@avanzu/types';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchPlans, NoPaymentMethodError, upgradeTier } from '../services/plans';
import { cancelSubscription, reactivateSubscription } from '../services/subscription';
import formatDate from '../utils/formatDate';
import PlanSelectorCoupon, { DiscountInfo } from './PlanSelectorCoupon';

interface PlanSelectorProps {
  currentSubscription?: SubscriptionData | null;
  onPlanChanged?: () => void | Promise<void>;
  onPaymentMethodRequired?: () => void;
  disabled?: boolean;
  showCurrentPlanChip?: boolean;
}

const PlanSelector: React.FC<PlanSelectorProps> = ({
  currentSubscription,
  onPlanChanged,
  onPaymentMethodRequired,
  disabled = false,
  showCurrentPlanChip = true,
}) => {
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [processingPlanId, setProcessingPlanId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [downgradeDialogOpen, setDowngradeDialogOpen] = useState<boolean>(false);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [promotionCode, setPromotionCode] = useState<string>('');
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo | null>(null);

  const { refreshUserOrganisationState, currentOrganisation } = useAuth();

  // Load available plans
  const loadPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPlans();

      // Sort plans by price (ascending)
      const sortedPlans = data.sort((a, b) => {
        const priceA = a.amount || 0;
        const priceB = b.amount || 0;
        return priceA - priceB;
      });
      setPlans(sortedPlans);
    } catch (err: any) {
      setError(err.message || 'Failed to load available plans');
    } finally {
      setLoading(false);
    }
  };

  // Check if plan change is a downgrade
  const isDowngrade = (priceId: string) => {
    if (!currentSubscription?.product || !currentSubscription?.price) {
      return false;
    }
    const plan = plans.find((p) => p.priceId === priceId);
    if (!plan) return false;

    const currentPlanAmount = currentSubscription.price.amount || 0;
    const newPlanAmount = plan.amount || 0;

    return newPlanAmount < currentPlanAmount;
  };

  // Handle plan selection with downgrade check
  const handleSelectPlan = async (priceId: string) => {
    if (isDowngrade(priceId)) {
      setPendingPlanId(priceId);
      setDowngradeDialogOpen(true);
      return;
    }

    // Proceed with plan change
    await executePlanChange(priceId);
  };

  // Execute the actual plan change (now: tier upgrade)
  const executePlanChange = async (priceId: string) => {
    const plan = plans.find((p) => p.priceId === priceId);
    if (!plan) {
      setError('Plan not found');
      return;
    }
    try {
      setProcessingPlanId(priceId);
      setError(null);
      await upgradeTier(plan.productId);
      await refreshUserOrganisationState();

      if (onPlanChanged) {
        await onPlanChanged();
      }
    } catch (err: any) {
      if (err instanceof NoPaymentMethodError) {
        if (onPaymentMethodRequired) {
          onPaymentMethodRequired();
        } else {
          setError('Please add a payment method before upgrading');
        }
      } else {
        setError(err.message || 'Failed to upgrade tier');
      }
    } finally {
      setProcessingPlanId(null);
    }
  };

  // Confirm downgrade
  const handleConfirmDowngrade = async () => {
    setDowngradeDialogOpen(false);
    if (pendingPlanId) {
      await executePlanChange(pendingPlanId);
      setPendingPlanId(null);
    }
  };

  // Cancel downgrade
  const handleCancelDowngrade = () => {
    setDowngradeDialogOpen(false);
    setPendingPlanId(null);
  };

  // Handle cancel plan button click
  const handleCancel = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setCancelDialogOpen(true);
  };

  // Confirm cancellation
  const handleConfirmCancel = async () => {
    setCancelDialogOpen(false);
    try {
      setActionLoading(true);
      setError(null);
      await cancelSubscription();

      // Refresh organisation state
      await refreshUserOrganisationState();

      if (onPlanChanged) {
        await onPlanChanged();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to cancel plan');
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel the cancellation dialog
  const handleCancelDialog = () => {
    setCancelDialogOpen(false);
  };

  // Handle reactivate plan
  const handleReactivate = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      setActionLoading(true);
      setError(null);
      await reactivateSubscription();
      await refreshUserOrganisationState();

      if (onPlanChanged) {
        await onPlanChanged();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reactivate plan');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    refreshUserOrganisationState();
    loadPlans();
  }, []);

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const calculateDiscountedPrice = (amount: number): number => {
    if (!discountInfo) return amount;

    if (discountInfo.percentOff) {
      return amount * (1 - discountInfo.percentOff / 100);
    }

    if (discountInfo.amountOff) {
      return Math.max(0, amount - discountInfo.amountOff * 100);
    }

    return amount;
  };

  const handlePromotionCodeApplied = (code: string, discount: DiscountInfo | null) => {
    setPromotionCode(code);
    setDiscountInfo(discount);
  };

  const formatQuotaValue = (value: string | undefined) => {
    if (!value) return 'N/A';
    const numValue = parseInt(value, 10);
    if (numValue === -1) return 'Unlimited';
    return numValue.toLocaleString();
  };

  const formatAiEnabled = (value: string | undefined) => {
    if (!value) return 'Not included';
    return value.toLowerCase() === 'true' || value === '1' ? 'Included' : 'Not included';
  };

  const getPlanActionLabel = (plan: PlanOption) => {
    if (!currentSubscription?.product || !currentSubscription?.price) {
      return 'Purchase';
    }

    if (currentSubscription.product.name === plan.name) {
      return 'Current Plan';
    }

    const currentPlanAmount = currentSubscription.price.amount || 0;
    const newPlanAmount = plan.amount || 0;

    if (newPlanAmount > currentPlanAmount) return 'Upgrade';
    if (newPlanAmount < currentPlanAmount) return 'Downgrade';
    return 'Change Plan';
  };

  const getPlanButtonVariant = (plan: PlanOption) => {
    if (!currentSubscription?.product || !currentSubscription?.price) {
      return 'contained';
    }
    const currentPlanAmount = currentSubscription.price.amount || 0;
    const newPlanAmount = plan.amount || 0;

    if (newPlanAmount < currentPlanAmount) return 'outlined';
    return 'contained';
  };

  const getPlanActionIcon = (plan: PlanOption) => {
    if (!currentSubscription?.product || !currentSubscription?.price) {
      return <CheckCircleIcon />;
    }

    if (currentSubscription.product.name === plan.name) {
      return <CheckCircleIcon />;
    }

    const currentPlanAmount = currentSubscription.price.amount || 0;
    const newPlanAmount = plan.amount || 0;

    if (newPlanAmount > currentPlanAmount) return <ArrowUpwardIcon />;
    if (newPlanAmount < currentPlanAmount) return <ArrowDownwardIcon />;
    return <CheckCircleIcon />;
  };

  const isCurrentPlan = (plan: PlanOption) => {
    // New (per-project) model: org has currentTierProductId. Legacy fallback
    // to subscription product name for any callers still using subscriptions.
    if (currentOrganisation?.currentTierProductId) {
      return currentOrganisation.currentTierProductId === plan.productId;
    }
    return currentSubscription?.product?.name === plan.name;
  };

  if (loading) {
    return <PlanSelectorSkeleton />;
  }

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Cancel Plan Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialog}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">Cancel Plan?</DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            Are you sure you want to cancel your plan? Your plan will remain active until the end
            of the current billing period, after which it will automatically cancel.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialog} color="primary">
            No, Keep Plan
          </Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained" autoFocus>
            Yes, Cancel Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Downgrade Plan Confirmation Dialog */}
      <Dialog
        open={downgradeDialogOpen}
        onClose={handleCancelDowngrade}
        aria-labelledby="downgrade-dialog-title"
        aria-describedby="downgrade-dialog-description"
      >
        <DialogTitle id="downgrade-dialog-title">Downgrade Plan?</DialogTitle>
        <DialogContent>
          <DialogContentText id="downgrade-dialog-description">
            Are you sure you want to downgrade your plan? This may reduce the number of projects,
            transactions and users you can use.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDowngrade} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDowngrade} color="warning" variant="contained" autoFocus>
            Yes, Downgrade
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.priceId}>
            <Box sx={{ position: 'relative', height: '100%' }}>
              {showCurrentPlanChip && isCurrentPlan(plan) && (
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    top: -12,
                  }}
                >
                  <Chip label="Current Plan" size="small" color="success" sx={{ zIndex: 1 }} />
                </Box>
              )}
              <Card
                sx={{
                  height: '100%',
                  border: isCurrentPlan(plan) ? 2 : 1,
                  padding: isCurrentPlan(plan) ? '0px' : '1px',
                  borderColor: isCurrentPlan(plan) ? 'success.main' : 'divider',
                  position: 'relative',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}
                >
                  {/* Top section - plan name, price, description */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {plan.name}
                    </Typography>
                    {plan.amount === 0 ? (
                      <Typography variant="h4" gutterBottom>
                        Free
                      </Typography>
                    ) : plan.amount ? (
                      <Box>
                        {/* Show crossed-out price when: 1) discount applied for non-current plans, or 2) current plan has coupon applied */}
                        {((discountInfo && !isCurrentPlan(plan)) ||
                          (isCurrentPlan(plan) &&
                            currentOrganisation?.monthlySubscriptionValue &&
                            currentOrganisation.monthlySubscriptionValue !== plan.amount)) && (
                          <Typography
                            variant="h6"
                            sx={{
                              textDecoration: 'line-through',
                              color: 'text.secondary',
                              display: 'inline',
                              mr: 1,
                            }}
                          >
                            {formatCurrency(plan.amount, plan.currency || 'aud')}
                          </Typography>
                        )}
                        <Typography
                          variant="h4"
                          sx={{
                            display: 'inline',
                            color:
                              (discountInfo && !isCurrentPlan(plan)) ||
                              (isCurrentPlan(plan) &&
                                currentOrganisation?.monthlySubscriptionValue &&
                                currentOrganisation.monthlySubscriptionValue !== plan.amount)
                                ? 'success.main'
                                : 'text.primary',
                          }}
                          gutterBottom
                        >
                          {formatCurrency(
                            isCurrentPlan(plan) && currentOrganisation?.monthlySubscriptionValue
                              ? currentOrganisation.monthlySubscriptionValue
                              : isCurrentPlan(plan)
                                ? plan.amount
                                : calculateDiscountedPrice(plan.amount),
                            plan.currency || 'aud'
                          )}
                          {plan.interval &&
                            !(discountInfo && !isCurrentPlan(plan) && discountInfo.duration === 'once') && (
                              <Typography component="span" variant="body2" color="text.secondary">
                                /{plan.interval}
                              </Typography>
                            )}
                          {discountInfo && !isCurrentPlan(plan) && discountInfo.duration === 'once' && (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {' '}
                              now
                            </Typography>
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: -0.5, mb: 1 }}
                        >
                          per financial year
                        </Typography>
                        {discountInfo &&
                          !isCurrentPlan(plan) &&
                          discountInfo.duration === 'once' &&
                          plan.interval && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5, mb: 1, fontWeight: 600 }}
                            >
                              (Then {formatCurrency(plan.amount, plan.currency || 'aud')}/
                              {plan.interval})
                            </Typography>
                          )}
                      </Box>
                    ) : (
                      <Typography variant="h4" gutterBottom>
                        Custom
                      </Typography>
                    )}
                    {plan.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {plan.description}
                      </Typography>
                    )}
                  </Box>

                  {/* Spacer to push bottom content down */}
                  <Box sx={{ flexGrow: 1 }} />

                  {isCurrentPlan(plan) && currentSubscription?.subscription?.cancelAtPeriodEnd && (
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                      <Chip
                        label={`Cancels on ${formatDate(currentSubscription.subscription.currentPeriodEnd || undefined)}`}
                        color="warning"
                        size="small"
                      />
                    </Box>
                  )}

                  {/* Bottom section - divider, quota, buttons */}
                  <Box>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ mb: 2, px: 0, py: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}
                      >
                        <span>Projects per year:</span>
                        <strong>{formatQuotaValue(plan.metadata?.quotaMaxProjectsPerYear)}</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}
                      >
                        <span>Project transactions:</span>
                        <strong>
                          {formatQuotaValue(plan.metadata?.quotaMaxTransactionsPerProject)}
                        </strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}
                      >
                        <span>Users:</span>
                        <strong>{formatQuotaValue(plan.metadata?.quotaMaxUsers)}</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ display: 'flex', justifyContent: 'space-between' }}
                      >
                        <span>AI features:</span>
                        <strong>{formatAiEnabled(plan.metadata?.quotaAiEnabled)}</strong>
                      </Typography>
                    </Box>

                    {plan.amount === 0 ? (
                      // Free tier is never actionable from here. Show status:
                      // - Selected if the org is on it
                      // - Trial Used once the trial has been consumed
                      // - Not available if the org is already on a paid tier
                      <Button variant="outlined" fullWidth disabled>
                        {isCurrentPlan(plan)
                          ? 'Selected'
                          : currentOrganisation?.hasUsedFreeTier
                            ? 'Trial Used'
                            : 'Not available'}
                      </Button>
                    ) : isCurrentPlan(plan) ? (
                      <Stack spacing={1}>
                        {currentSubscription?.subscription?.status === 'canceled' ? (
                          <Button
                            variant="outlined"
                            color="success"
                            fullWidth
                            startIcon={
                              actionLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />
                            }
                            onClick={handleReactivate}
                            disabled={actionLoading || disabled}
                          >
                            Reactivate
                          </Button>
                        ) : currentSubscription?.subscription?.cancelAtPeriodEnd ? (
                          <Button
                            variant="outlined"
                            color="success"
                            fullWidth
                            startIcon={
                              actionLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />
                            }
                            onClick={handleReactivate}
                            disabled={actionLoading || disabled}
                          >
                            Resume Plan
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={
                              actionLoading ? <CircularProgress size={20} /> : <CancelIcon />
                            }
                            onClick={handleCancel}
                            disabled={actionLoading || disabled}
                          >
                            Cancel Plan
                          </Button>
                        )}
                      </Stack>
                    ) : plan.amount ? (
                      <Button
                        variant={getPlanButtonVariant(plan)}
                        fullWidth
                        onClick={() => handleSelectPlan(plan.priceId)}
                        disabled={
                          processingPlanId === plan.priceId || processingPlanId !== null || disabled
                        }
                        startIcon={
                          processingPlanId === plan.priceId ? (
                            <CircularProgress size={20} />
                          ) : (
                            getPlanActionIcon(plan)
                          )
                        }
                        color="primary"
                      >
                        {processingPlanId === plan.priceId
                          ? 'Processing...'
                          : getPlanActionLabel(plan)}
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        fullWidth
                        disabled={processingPlanId !== null || disabled}
                      >
                        Contact Sales
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Coupon Section */}
      <PlanSelectorCoupon onPromotionCodeApplied={handlePromotionCodeApplied} />
    </>
  );
};

// Skeleton component for loading state
const PlanSelectorSkeleton: React.FC = () => {
  return (
    <Grid container spacing={3}>
      {/* Generate 3 skeleton plan cards */}
      {[0, 1, 2].map((index) => (
        <Grid item xs={12} md={4} key={index}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Skeleton variant="text" width={120} height={28} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={150} height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="90%" height={20} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="80%" height={20} />

              {/* Features list */}
              <List dense sx={{ my: 2 }}>
                {[0, 1, 2, 3].map((featureIndex) => (
                  <ListItem key={featureIndex} disableGutters>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                    </ListItemIcon>
                    <Skeleton variant="text" width="90%" height={20} />
                  </ListItem>
                ))}
              </List>

              {/* Action button */}
              <Skeleton variant="rectangular" width="100%" height={36} sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default PlanSelector;
